"use client"

import { ComponentConfig } from "@measured/puck"
import { useEffect, useRef, useState } from "react"
import "swiper/css"
import "swiper/css/pagination"
import { generateImagePath } from "../../../../../../utils/generateImagePath"
import { DefaultImage } from "../../../../external-components"
import { CommonStylesProps } from "../../../lib/commonCSSProps"
import { generateResponsiveCSS } from "../../../lib/helper"

export interface SliderItem extends CommonStylesProps {
  image1: string
  image2?: string
  textField: {
    country: string
    countryTextSize: string
    countryTextWeight: string
    countryLineHeight: string
    province: string
    provinceTextSize: string
    provinceTextWeight: string
    provinceLineHeight: string
  }
  textContainerStyles?: {
    position?: "top-left" | "top-right" | "bottom-left" | "bottom-right" | "center"
    fontSize?: string
    fontWeight?: "light" | "normal" | "bold" | "extrabold"
    color?: string
    backgroundColor?: string
    padding?: string
    gap?: string
  }
  mediaStyles?: {
    width: string
    margin: string
  }
}

export interface SliderProps extends CommonStylesProps {
  type: "fluid" | "custom" | "boxed"
  sliderItems?: SliderItem[]
  controlButton: boolean
  heightSliderDesktop?: string
  heightSliderMobile?: string
  gapSlider?: string
}

export const RenderConfig: ComponentConfig<SliderProps> = {
  render: ({
    className,
    type,
    controlButton,
    styles,
    gapSlider,
    responsiveType,
    heightSliderDesktop,
    heightSliderMobile,
    sliderItems = [],
  }) => {
    const id = `navigation-${Math.random().toString(36).substr(2, 9)}`
    const [currentIndex, setCurrentIndex] = useState(0)
    const [hoveredIndex, setHoveredIndex] = useState<number | null>(null)
    const positionClasses = {
      "top-left": "absolute top-0 left-0",
      "top-right": "absolute top-0 right-0",
      "bottom-left": "absolute bottom-0 left-0",
      "bottom-right": "absolute bottom-0 right-0",
      center: "absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2",
    }
    const sliderRef = useRef<HTMLDivElement>(null)
    const [isDragging, setIsDragging] = useState(false)
    const [startX, setStartX] = useState(0)
    const [scrollLeft, setScrollLeft] = useState(0)

    const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
      if (!sliderRef.current) return

      e.preventDefault()

      setIsDragging(true)
      setStartX(e.pageX - sliderRef.current.offsetLeft)
      setScrollLeft(sliderRef.current.scrollLeft)

      sliderRef.current.style.userSelect = "none"
    }

    const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
      if (!isDragging || !sliderRef.current) return

      const x = e.pageX - sliderRef.current.offsetLeft
      const walk = (x - startX) * 2
      sliderRef.current.scrollLeft = scrollLeft - walk
    }

    const handleMouseUp = () => {
      if (!sliderRef.current) return

      setIsDragging(false)
      sliderRef.current.style.userSelect = "auto"
    }
    const handleMouseLeave = () => {
      if (!sliderRef.current) return

      setIsDragging(false)
      sliderRef.current.style.userSelect = "auto"
    }
    const handleTouchStart = (e: React.TouchEvent<HTMLDivElement>) => {
      if (!sliderRef.current) return

      const touch = e.touches[0]
      setIsDragging(true)
      setStartX(touch.pageX - sliderRef.current.offsetLeft)
      setScrollLeft(sliderRef.current.scrollLeft)

      sliderRef.current.style.userSelect = "none"
    }

    const handleTouchMove = (e: React.TouchEvent<HTMLDivElement>) => {
      if (!isDragging || !sliderRef.current) return

      const touch = e.touches[0]
      const x = touch.pageX - sliderRef.current.offsetLeft
      const walk = (x - startX) * 2 // Adjust sensitivity
      sliderRef.current.scrollLeft = scrollLeft - walk
    }

    const handleTouchEnd = () => {
      if (!sliderRef.current) return

      setIsDragging(false)
      sliderRef.current.style.userSelect = "auto"
    }

    const scrollToCenter = (direction: "next" | "prev") => {
      if (!sliderRef.current || sliderItems.length === 0) return

      const slider = sliderRef.current
      const sliderWidth = slider.offsetWidth
      const scrollLeft = slider.scrollLeft

      const itemWidth = slider.scrollWidth / sliderItems.length
      const visibleCenter = scrollLeft + sliderWidth / 2

      const currentIndex = Math.floor(visibleCenter / itemWidth)

      let newIndex =
        direction === "next" ? Math.min(currentIndex + 1, sliderItems.length - 1) : Math.max(currentIndex - 1, 0)

      const newScrollPosition = newIndex * itemWidth - sliderWidth / 2 + itemWidth / 2

      slider.scrollTo({
        left: newScrollPosition,
        behavior: "smooth",
      })

      setCurrentIndex(newIndex)
    }

    const mergedCSS = {
      mobile: { ...styles?.mobile },
      tablet: { ...styles?.tablet },
      desktop: { ...styles?.desktop },
    }
    const responsiveCSS = generateResponsiveCSS(id, mergedCSS || {}, responsiveType)
    console.log("height desktop", heightSliderDesktop)
    const handleScroll = () => {
      if (!sliderRef.current) return
      const slider = sliderRef.current
      const itemWidth = slider.scrollWidth / sliderItems.length
      const visibleCenter = slider.scrollLeft + slider.offsetWidth / 2

      const newIndex = Math.floor(visibleCenter / itemWidth)
      setCurrentIndex(newIndex)
    }

    useEffect(() => {
      const slider = sliderRef.current
      if (!slider) return

      slider.addEventListener("scroll", handleScroll)
      return () => slider.removeEventListener("scroll", handleScroll)
    }, [sliderItems])
    return (
      <>
        {responsiveCSS}
        <div id={id} className="relative w-full">
          <div
            ref={sliderRef}
            className="flex overflow-x-scroll gap-6 snap-x snap-mandatory scrollbar-hidden scroll-smooth touch-pan-x"
            style={{
              overflow: "hidden",
              cursor: isDragging ? "grabbing" : "grab",
            }}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseLeave}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          >
            {sliderItems.map((item, index) => (
              <div
                key={index}
                className={`snap-center flex-shrink-0 relative`}
                style={{
                  width:
                    window.innerWidth >= 1024
                      ? item.mediaStyles?.width
                      : window.innerWidth <= 1024 && parseInt(String(item.mediaStyles?.width), 10) > 500
                      ? "375px"
                      : item.mediaStyles?.width,
                  height: window.innerWidth >= 1024 ? heightSliderDesktop : heightSliderMobile || "auto",
                }}
                onMouseEnter={() => setHoveredIndex(index)}
                onMouseLeave={() => setHoveredIndex(null)}
              >
                {item.textField && item.textContainerStyles && (
                  <div
                    className={`absolute ${
                      positionClasses[item.textContainerStyles.position || "center"]
                    } flex flex-col `}
                    style={{
                      color: item.textContainerStyles.color || "white",
                      gap: item.textContainerStyles.gap || "8px",
                      padding: item.textContainerStyles.padding || "24px",
                      zIndex: 20,
                    }}
                  >
                    {item.textField.country && (
                      <div
                        style={{
                          fontSize: item.textField.countryTextSize || "16px",
                          fontWeight: item.textField.countryTextWeight || "400",
                          lineHeight: item.textField.countryLineHeight || "19px",
                        }}
                      >
                        {item.textField.country}
                      </div>
                    )}
                    {item.textField.province && (
                      <div
                        style={{
                          fontSize: item.textField.provinceTextSize || "28px",
                          fontWeight: item.textField.provinceTextWeight || "700",
                          lineHeight: item.textField.provinceLineHeight || "32px",
                        }}
                      >
                        {item.textField.province}
                      </div>
                    )}
                  </div>
                )}
                <img
                  src={item.image1 ? generateImagePath(item.image1, "medium") : DefaultImage}
                  alt={`Slide ${index + 1}`}
                  className={`lg:h-full  lg:rounded-2xl ${
                    parseInt(String(item.mediaStyles?.width), 10) < 500 && "rounded-2xl"
                  } bg-contain object-cover`}
                  style={{
                    width:
                      window.innerWidth >= 1024
                        ? item.mediaStyles?.width
                        : window.innerWidth <= 1024 && parseInt(String(item.mediaStyles?.width), 10) > 500
                        ? "375px"
                        : item.mediaStyles?.width,
                    height: window.innerWidth >= 1024 ? "100%" : heightSliderMobile,
                    transition: "all 300ms cubic-bezier(0.4, 0, 0.2, 1)",
                  }}
                />

                {item.textField?.country && item.textField?.province && (
                  <div
                    className="absolute bottom-0 left-0 w-full rounded-b-2xl"
                    style={{
                      height: "107px",
                      background: "linear-gradient(to top, #000000CC, #242F3E00)",
                      zIndex: 10,
                    }}
                  ></div>
                )}
                {item.image2 && (
                  <>
                    <img
                      src={item.image2 ? generateImagePath(item.image2, "medium") : DefaultImage}
                      alt={`Slide ${index + 1} hover`}
                      className={`absolute top-0 left-0 w-full h-full object-cover rounded-2xl transition-opacity duration-300 ${
                        hoveredIndex === index ? "opacity-100" : "opacity-0"
                      }`}
                      style={{
                        transition: "all 300ms cubic-bezier(0.4, 0, 0.2, 1)",
                      }}
                    />
                  </>
                )}

                {hoveredIndex === index && item.image2 && (
                  <button
                    className="absolute bottom-[33.5px] right-6 bg-[#FFFFFF33] text-white p-3 rounded-full z-30 backdrop-blur-sm"
                    style={{
                      boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.1)",
                      transition: "transform 300ms ease-in-out",
                    }}
                  >
                    <svg width="16" height="16" viewBox="0 0 16 17" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path
                        d="M5.77734 2.94434L11.3329 8.49989L5.77734 14.0554"
                        stroke="white"
                        stroke-width="1.5"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                    </svg>
                  </button>
                )}
              </div>
            ))}
          </div>
          {controlButton && (
            <div className=" justify-center gap-2 lg:mt-16 lg:flex hidden">
              <button
                className="flex p-1 rounded-full w-10 h-10 items-center justify-center"
                style={{ backgroundColor: "#EBEDF0" }}
                onClick={() => scrollToCenter("prev")}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#000000"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  className="lucide lucide-chevron-left"
                >
                  <path d="m15 18-6-6 6-6" />
                </svg>
              </button>
              <button
                className="flex p-1 rounded-full w-10 h-10 items-center justify-center"
                style={{ backgroundColor: "#EBEDF0" }}
                onClick={() => scrollToCenter("next")}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#000000"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  className="lucide lucide-chevron-right"
                >
                  <path d="m9 18 6-6-6-6" />
                </svg>
              </button>
            </div>
          )}
          {sliderItems.length > 1 && parseInt(String(heightSliderMobile), 10) > 200 && (
            <div className="flex items-center w-full justify-center lg:hidden">
              <div className="flex justify-center mt-4 bg-[#242F3E0F] py-[10px] px-2 rounded-full">
                {sliderItems.map((_, index) => (
                  <div
                    key={index}
                    className={`h-2 w-2 mx-[5px] rounded-full transition-all ${
                      currentIndex === index ? "bg-[#000] w-6" : "bg-[#D9D9D9]"
                    }`}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      </>
    )
  },
}
